/**
 * Owner-only Book mapping control for the existing Tome I content workspace.
 * It renders outside the four approved spaces; no navigation/MASTER UI change.
 */
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { inspectAlmondBookTree, connectAlmondBookTree } from "@/lib/almondBookMappingV1.mjs";
import { TomeIntroductionLinkControlV1 } from "./TomeIntroductionLinkControlV1";

export function AlmondBookMappingControlV1({ product, onConnected }) {
  const [preflight, setPreflight] = useState(null);
  const [checking, setChecking] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");
  const [connected, setConnected] = useState(false);

  const check = async () => {
    if (checking || connecting) return;
    setChecking(true);
    setPreflight(null);
    setError("");
    try {
      const next = await inspectAlmondBookTree(api, product);
      setPreflight(next);
      setConnected(next.volume && next.missingSpaces === 0 && next.missingCategories === 0);
    } catch (e) {
      setError(e.message || formatApiError(e));
    } finally {
      setChecking(false);
    }
  };

  const connect = async () => {
    if (checking || connecting || !preflight || connected) return;
    setConnecting(true);
    setError("");
    try {
      const next = await connectAlmondBookTree(api, product);
      setPreflight(next);
      setConnected(true);
      onConnected?.();
    } catch (e) {
      // A partial request can leave draft-only entries. Recheck by GET before
      // trying again, never blindly retry POST on a stale selected state.
      setPreflight(null);
      setError(e.message || formatApiError(e));
    } finally {
      setConnecting(false);
    }
  };

  return (
    <>
    <div className="mt-5 rounded-xl border border-[#D7C5AD] bg-white/40 p-4 text-sm text-[#644B36]"
      data-testid="almond-book-mapping-owner">
      <div className="font-serif text-lg text-[#39261B]">Связь содержания с Book</div>
      <p className="mt-1 text-xs leading-5">
        Только техническая привязка уже утверждённых четырёх пространств и 31 категории.
        Новые статьи, Book-страницы, публикации и читательский доступ не создаются.
      </p>
      <button type="button" onClick={check} disabled={checking || connecting}
        data-testid="almond-book-mapping-check"
        className="mt-3 rounded-lg border border-[#9B7B59]/40 bg-white/60 px-4 py-2 disabled:opacity-50">
        {checking ? "Проверяем…" : "Проверить структуру Book"}
      </button>
      {preflight && (
        <div className="mt-3 text-xs" data-testid="almond-book-mapping-state">
          {connected ? "Структура связана: 4 пространства, 31 категория."
            : `Не подключено: ${preflight.missingSpaces} пространств и ${preflight.missingCategories} категорий.`}
          {!connected && (
            <div className="mt-3">
              <p className="mb-2">Подключение создаст только скрытую черновую структуру Book.
                Подтвердите действие отдельной кнопкой. Повторное подключение проверяет существующие ID.</p>
              <button type="button" onClick={connect} disabled={connecting || checking}
                data-testid="almond-book-mapping-connect"
                className="rounded-lg bg-[#4A3425] px-4 py-2 text-white disabled:opacity-50">
                {connecting ? "Связываем…" : "Подключить структуру Тома"}
              </button>
            </div>
          )}
        </div>
      )}
      {error && <p role="alert" className="mt-3 text-xs text-red-800">{error}</p>}
    </div>
    <TomeIntroductionLinkControlV1 product={product} />
    </>
  );
}
